// src/ConsumerApp/Screens/Account/views/OrdersMasterDetailView.js
import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetOrdersByCustomer, useGetOrderById } from '../../../api/orders/orders.hooks';
import OrderCard from '../components/OrderCard';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import Package from '../../../../assets/icons/Package.svg';
import Lightning from '../../../../assets/icons/Lightning.svg';
import ClipboardText from '../../../../assets/icons/ClipboardText.svg';
import BuildingOffice from '../../../../assets/icons/BuildingOffice.svg';
import MagnifyingGlass from '../../../../assets/icons/MagnifyingGlass.svg';
import Key from '../../../../assets/icons/Key.svg';
import CaretDown from '../../../../assets/icons/CaretDown.svg';
import CaretUp from '../../../../assets/icons/CaretUpBlack.svg';
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import CheckCircleGreen from '../../../../assets/icons/CheckCircleGreen.svg';
import CheckCircleGray from '../../../../assets/icons/CheckCircleGray.svg';
import Truck from '../../../../assets/icons/Truck.svg';
import GrayTruck from '../../../../assets/icons/GrayTruck.svg';
import ShootingStar from '../../../../assets/icons/ShootingStar.svg';
import CurrencyInr from '../../../../assets/icons/CurrencyInr.svg';
import Prohibit from '../../../../assets/icons/Prohibit.svg';
import Receipt from '../../../../assets/icons/Receipt.svg';
import Phone from '../../../../assets/icons/Phone.svg';
import ChatCircle from '../../../../assets/icons/ChatCircle.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;
const SUPPORT_PHONE_NUMBER = '+918247895454';
const WHATSAPP_PHONE_NUMBER = '918639065454';

// Tracking configuration from OrderDetailsScreen.js[cite: 12]
const INSTANT_STEPS = [
  { id: 'CONFIRMED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'Shipment Created', activeDesc: 'Order confirmed and waiting for schedule.', upcomingDesc: 'Waiting for delivery schedule.' },
  { id: 'PACKING', icon: Package, iconPending: Package, title: 'Items Packed', activeDesc: 'Your items are packed and ready.', upcomingDesc: 'Ready for delivery.' },
  { id: 'ON_THE_WAY', icon: Truck, iconPending: GrayTruck, title: 'Out for delivery', activeDesc: 'Your order is on the way.', upcomingDesc: 'Out for delivery.' },
  { id: 'DELIVERED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'Delivered successfully', activeDesc: 'Order has been handed to you successfully.', upcomingDesc: 'Delivered successfully.' }
];

const RFQ_STEPS = [
  { id: 'SUBMITTED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'RFQ submitted', activeDesc: 'Request sent successfully.', upcomingDesc: 'Request sent successfully.' },
  { id: 'QUOTE_RECEIVED', icon: ShootingStar, iconPending: ShootingStar, title: 'Best quotation received', activeDesc: 'Pay now to lock the special price.', upcomingDesc: 'Best quote confirmed.' },
  { id: 'PAYMENT', icon: CurrencyInr, iconPending: CurrencyInr, title: 'Payment completed', activeDesc: 'Payment completed successfully.', upcomingDesc: 'Complete payment to place order.' },
  { id: 'PACKING', icon: Package, iconPending: Package, title: 'Packing', activeDesc: 'Your order is being packed.', upcomingDesc: 'Order starts packing.' },
  { id: 'ON_THE_WAY', icon: Truck, iconPending: Truck, title: 'On the way', activeDesc: 'Reaching you today.', upcomingDesc: 'Truck dispatch.' },
  { id: 'DELIVERED', icon: CheckCircleGreen, iconPending: CheckCircleGray, title: 'Delivered successfully', activeDesc: 'Order has been handed to you successfully.', upcomingDesc: 'Order has been handed to you successfully.' }
];

const mapStatusToKey = (statusStr) => {
  if (!statusStr) return 'CONFIRMED';
  const s = statusStr.toLowerCase();
  if (s.includes('quote')) return 'QUOTE_RECEIVED';
  if (s.includes('waiting') || s.includes('confirmed')) return 'CONFIRMED';
  if (s.includes('ready') || s.includes('packing')) return 'PACKING';
  if (s.includes('out') || s.includes('way') || s.includes('delivering')) return 'ON_THE_WAY';
  if (s.includes('delivered')) return 'DELIVERED';
  return 'CONFIRMED';
};

// Delivery Group Component ported from mobile DeliveryGroupCard[cite: 12]
function DeliveryGroupSection({ group, isRfq, orderStatus, orderData }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const isCancelled = orderStatus === 'Cancelled';

  const baseSteps = isRfq ? RFQ_STEPS : INSTANT_STEPS;
  const displaySteps = isCancelled
    ? [...baseSteps, { id: 'CANCELLED', title: 'Canceled', activeDesc: 'Order closed via cancellation.', upcomingDesc: 'Order closed via cancellation.' }]
    : baseSteps;

  const groupStatus = isCancelled ? 'CANCELLED' : mapStatusToKey(group.delivery_group_status);
  const currentStepIndex = Math.max(0, displaySteps.findIndex((s) => s.id === groupStatus));
  const activeStep = displaySteps[currentStepIndex];

  const totalItemsCount = group.delivery_categories?.reduce(
    (total, cat) => total + (cat.items?.length || 0),
    0
  ) || 0;

  const showEta = !group.order_delivered_otp && !isCancelled && groupStatus !== 'DELIVERED';
  const etaText = orderData.delivery_eta
    ? `${orderData.delivery_eta.minMinutes} - ${orderData.delivery_eta.maxMinutes} mins`
    : orderData.delivery_slot?.label || group.estimated_delivery_time || '30 - 90 mins';

  return (
    <div style={detailsStyles.deliveryGroupCard}>
      {/* OTP Display Card[cite: 12] */}
      {group?.order_delivered_otp && group.delivery_group_status !== 'Delivered' && (
        <div style={detailsStyles.otpCard}>
          <div style={detailsStyles.otpHeaderRow}>
            <div style={detailsStyles.otpIconBox}>
              <img src={Key} alt="Key" style={{ width: 18, height: 18 }} />
            </div>
            <span style={detailsStyles.otpTitle}>
              Share this OTP with the delivery partner after checking your items.
            </span>
          </div>
          <div style={detailsStyles.otpValueBox}>
            <span style={detailsStyles.otpValueLabel}>Delivery OTP</span>
            <span style={detailsStyles.otpValue}>{group.order_delivered_otp}</span>
          </div>
        </div>
      )}

      {/* Tracking Card with Collapsible Caret[cite: 12] */}
      <div style={detailsStyles.trackingCard}>
        <div style={detailsStyles.trackingHeader} onClick={() => setIsExpanded(!isExpanded)}>
          <div>
            {showEta ? (
              <div style={detailsStyles.etaRow}>
                <img src={Lightning} alt="Lightning" style={{ width: 16, height: 16 }} />
                <span style={detailsStyles.etaText}>Arriving in {etaText}</span>
              </div>
            ) : (
              <span style={detailsStyles.trackingTitle}>
                {isCancelled ? 'Order Cancelled' : activeStep?.title || 'Delivery Status'}
              </span>
            )}
          </div>
          <div style={{ cursor: 'pointer' }}>
            <img src={isExpanded ? CaretDown : CaretUp} alt="toggle" style={{ width: 12, height: 12 }} />
          </div>
        </div>

        {/* Step Nodes[cite: 12] */}
        {isExpanded && (
          <div style={detailsStyles.stepsContainer}>
            {displaySteps.map((stepConfig, index) => {
              const isCancelStep = stepConfig.id === 'CANCELLED';
              const isSkipped = isCancelled && index > 0 && !isCancelStep;
              const isCompleted = !isCancelled && index < currentStepIndex;
              const isActive = index === currentStepIndex;
              const isUpcoming = !isCancelled && index > currentStepIndex;
              const isLast = index === displaySteps.length - 1;

              const StepIcon = isCancelStep
                ? Prohibit
                : (isCompleted || isActive || (isCancelled && index === 0))
                ? CheckCircleGreen
                : (stepConfig.iconPending || CheckCircleGray);

              return (
                <div key={stepConfig.id} style={detailsStyles.stepRow}>
                  {/* Left Column: Icon + Dotted Line */}
                  <div style={detailsStyles.stepIconCol}>
                    <div
                      style={{
                        ...detailsStyles.stepIconCircle,
                        backgroundColor: isCancelStep
                          ? '#FFF0ED'
                          : isCompleted || isActive
                          ? '#E8F5EE'
                          : '#F5F3EF',
                      }}
                    >
                      <img src={StepIcon} alt="step" style={{ width: 14, height: 14 }} />
                    </div>
                    {!isLast && (
                      <div style={detailsStyles.dotsCol}>
                        {[...Array(4)].map((_, i) => (
                          <div
                            key={i}
                            style={{
                              ...detailsStyles.dot,
                              backgroundColor: isCompleted && !isCancelled ? '#1E8449' : '#CBD5E1',
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right Column: Title and Subtitle[cite: 12] */}
                  <div style={detailsStyles.stepTextCol}>
                    <span
                      style={{
                        ...detailsStyles.stepTitle,
                        color: isCancelStep ? '#E95A1E' : isUpcoming || isSkipped ? '#707B8C' : '#2B2A29',
                        textDecoration: isSkipped ? 'line-through' : 'none',
                      }}
                    >
                      {stepConfig.title}
                    </span>
                    <span style={detailsStyles.stepDesc}>
                      {isActive || isCancelStep ? stepConfig.activeDesc : stepConfig.upcomingDesc}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Items Box[cite: 12] */}
      <div style={detailsStyles.itemsBox}>
        <span style={detailsStyles.itemsCountLabel}>
          {totalItemsCount === 1 ? 'Item' : 'Items'} · {totalItemsCount}
        </span>

        {group.delivery_categories?.map((cat) => (
         <div key={cat.product_category_id || Math.random()}>
  {cat.items?.map((item, idx) => {
    const imgUrl = item.item_image ? `${IMAGE_BASE_URL}${item.item_image}` : null;
    return (
      <div key={item.item_id || idx} style={detailsStyles.itemLine}>
        <div style={detailsStyles.itemThumbBox}>
          <OrderItemImage src={imgUrl} alt={item.item_name} />
        </div>
        <div style={detailsStyles.itemMetaCol}>
          <div style={detailsStyles.itemTitleRow}>
            <span style={detailsStyles.itemTitleText}>{item.item_name}</span>
            <span style={detailsStyles.itemPriceText}>
              ₹{Number(item.total || item.price || 0).toLocaleString('en-IN')}
            </span>
          </div>
          <span style={detailsStyles.itemQtyText}>
            Qty: {item.quantity} {item.UOM || ''}
          </span>
        </div>
      </div>
    );
  })}
</div>
        ))}
      </div>
    </div>
  );
}

function OrderItemImage({ src, alt }) {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <div style={detailsStyles.itemThumbFallback}>
        <img
          src={ToteIcon}
          alt="Item fallback"
          style={{ width: 20, height: 20, opacity: 0.4 }}
        />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={detailsStyles.itemThumbImg}
      onError={() => setHasError(true)}
    />
  );
}

export default function OrdersMasterDetailView() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  // Retrieve user session
  const customer = useMemo(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const customerId = customer?._id;

  // Tabs and Filters matching mobile OrdersScreen.js[cite: 11]
  const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'ONGOING' | 'CLOSED'
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');

  // Queries
  const { data: rawOrdersData, isLoading: isOrdersLoading } = useGetOrdersByCustomer(customerId);
  const { data: rawOrderDetail, isLoading: isDetailLoading } = useGetOrderById(orderId);
  console.log(rawOrderDetail)

  const apiOrders = useMemo(() => {
    const list = rawOrdersData?.orders || rawOrdersData || [];
    return Array.isArray(list) ? list : [];
  }, [rawOrdersData]);

  const ongoingCount = useMemo(() => apiOrders.filter((o) => o.is_live_order === true).length, [apiOrders]);
  const closedCount = useMemo(() => apiOrders.filter((o) => o.is_live_order === false).length, [apiOrders]);

  // Derived filtered orders matching mobile logic[cite: 11]
  const displayedOrders = useMemo(() => {
    return apiOrders.filter((order) => {
      if (activeTab === 'ONGOING' && !order.is_live_order) return false;
      if (activeTab === 'CLOSED' && order.is_live_order) return false;

      const orderType = order.type || 'Buy Now';
      if (activeFilter !== 'All' && orderType !== activeFilter) return false;

      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase();
        const matchesNum = (order.order_number || order.salesorder_id || '').toLowerCase().includes(q);
        const matchesItems = order.items?.some((item) =>
          (item.item_name || item.product_name || '').toLowerCase().includes(q)
        );
        return matchesNum || matchesItems;
      }

      return true;
    });
  }, [apiOrders, activeTab, activeFilter, searchQuery]);

  const filterChips = [
    { label: 'All', icon: null },
    { label: 'Buy Now', icon: Lightning },
    { label: 'RFQ', icon: ClipboardText },
    { label: 'Project Quotes', icon: BuildingOffice },
  ];

  // -------------------------------------------------------------
  // VIEW: SINGLE ORDER DETAILS
  // -------------------------------------------------------------
  if (orderId) {
    const order =
      rawOrderDetail?.order ||
      rawOrderDetail ||
      apiOrders.find((o) => (o.order_id || o._id || o.salesorder_id) === orderId) ||
      {};

    if (isDetailLoading && !order.order_number) {
      return (
        <div style={detailsStyles.loadingContainer}>
          <span style={{ color: '#707B8C', fontSize: 14 }}>Fetching order details...</span>
        </div>
      );
    }

    const deliveryGroups = Array.isArray(order.delivery_groups) ? order.delivery_groups : [];
    const isRfq = order.type === 'RFQ' || order.type === 'Project Quote';
    const address = order.delivery_address || order.address || {};

    const formattedDate = order.order_date
      ? new Date(order.order_date).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      : '--';

    return (
      <div style={detailsStyles.wrapper}>
        {/* Header with back navigation and order date[cite: 12] */}
        <div style={detailsStyles.headerRow}>
          <button style={detailsStyles.backBtn} onClick={() => navigate('/consumer/account/orders')}>
            ←
          </button>
          <div>
            <h2 style={detailsStyles.orderNumberTitle}>{order.order_number || orderId}</h2>
            <span style={detailsStyles.orderDateSubtitle}>{formattedDate}</span>
          </div>
        </div>

        {/* Delivering To Address Card[cite: 12] */}
        <div style={detailsStyles.addressCard}>
          <div style={detailsStyles.addressCardHeader}>
            <span style={detailsStyles.addressCardHeaderTitle}>
              Delivering to: {address.customer_name || customer?.customer_name || 'Site Address'}
            </span>
          </div>
          <div style={detailsStyles.addressCardContent}>
            <p style={detailsStyles.addressText}>
              {address.area_sector_locality || address.address_line_1 || address.complete_address || 'Address provided at order placement'}
              {address.city ? `, ${address.city}` : ''}
              {address.pincode ? ` - ${address.pincode}` : ''}
            </p>
            <span style={detailsStyles.addressPhone}>
              {address.contact_mobile ? `+91 ${address.contact_mobile}` : customer?.mobile_number || ''}
            </span>
          </div>
        </div>

        {/* Delivery Groups Stream[cite: 12] */}
        {deliveryGroups.length > 0 ? (
          deliveryGroups.map((group, gIdx) => (
            <DeliveryGroupSection
              key={group.delivery_group_id || gIdx}
              group={group}
              isRfq={isRfq}
              orderStatus={order.status}
              orderData={order}
            />
          ))
        ) : (
          <DeliveryGroupSection
            group={{
              delivery_group_status: order.salesorder_status || order.status,
              delivery_categories: [{ items: order.items || [] }],
            }}
            isRfq={isRfq}
            orderStatus={order.status}
            orderData={order}
          />
        )}

        {/* Bill Summary[cite: 12] */}
        <div style={detailsStyles.summaryCard}>
          <h3 style={detailsStyles.summaryTitle}>Bill Summary</h3>
          <div style={detailsStyles.summaryRow}>
            <span>Sub total</span>
            <span style={{ fontWeight: '600' }}>₹{Number(order.subtotal || order.total || 0).toLocaleString('en-IN')}</span>
          </div>
          {order.discount > 0 && (
            <div style={detailsStyles.summaryRow}>
              <span>Discount</span>
              <span style={{ color: '#1E8449', fontWeight: '600' }}>- ₹{order.discount}</span>
            </div>
          )}
          {order.delivery_charges > 0 && (
            <div style={detailsStyles.summaryRow}>
              <span>Delivery Charges</span>
              <span style={{ fontWeight: '600' }}>₹{order.delivery_charges}</span>
            </div>
          )}
          {order.tax_amount > 0 && (
            <div style={detailsStyles.summaryRow}>
              <span>Taxes</span>
              <span style={{ fontWeight: '600' }}>₹{order.tax_amount}</span>
            </div>
          )}
          <div style={detailsStyles.receiptDivider} />
          <div style={{ ...detailsStyles.summaryRow, marginBottom: 0, fontSize: 16 }}>
            <strong>Total</strong>
            <strong>₹{Number(order.total || 0).toLocaleString('en-IN')}</strong>
          </div>
        </div>

        {/* Invoice Action Strip[cite: 12] */}
        {order.invoice_generated && (
          <div style={detailsStyles.invoiceButton}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={Receipt} alt="Invoice" style={{ width: 20, height: 20 }} />
              <span style={{ fontWeight: '600', fontSize: 13 }}>View invoice details</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#707B8C' }}>{order.order_number}</span>
              <img src={CaretRight} alt="Arrow" style={{ width: 14, height: 14 }} />
            </div>
          </div>
        )}

        {/* Help with Order Strip[cite: 12] */}
        <div style={detailsStyles.helpCard}>
          <span style={detailsStyles.helpText}>Need help with this Order?</span>
          <div style={detailsStyles.helpButtonGroup}>
            <button
              style={detailsStyles.helpBtn}
              onClick={() => {
                const msg = encodeURIComponent('Hi, I need help with my order ' + (order.order_number || ''));
                window.open(`https://wa.me/${WHATSAPP_PHONE_NUMBER}?text=${msg}`, '_blank');
              }}
            >
              <img src={ChatCircle} alt="chat" style={{ width: 16, height: 16 }} />
              <span>Chat</span>
            </button>
            <button
              style={detailsStyles.helpBtn}
              onClick={() => (window.location.href = `tel:${SUPPORT_PHONE_NUMBER}`)}
            >
              <img src={Phone} alt="call" style={{ width: 16, height: 16 }} />
              <span>Call</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // VIEW: ORDERS LIST
  // -------------------------------------------------------------
  return (
    <div style={listStyles.wrapper}>
      {/* Header */}
      <div style={listStyles.headerRow}>
        <h2 style={listStyles.pageTitle}>My Orders</h2>
        <span style={listStyles.orderCountText}>{apiOrders.length} total</span>
      </div>

      {/* Controls: Search Bar, Ongoing/Closed, and Type Chips[cite: 11] */}
      <div style={listStyles.controls}>
        {/* Search Bar with MagnifyingGlass SVG[cite: 11] */}
        <div style={listStyles.searchContainer}>
          <img src={MagnifyingGlass} alt="search" style={{ width: 16, height: 16, opacity: 0.6 }} />
          <input
            type="text"
            placeholder="Search by order ID or item name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={listStyles.searchInput}
          />
          {searchQuery && (
            <button style={listStyles.clearBtn} onClick={() => setSearchQuery('')}>
              ✕
            </button>
          )}
        </div>

        {/* Ongoing / Closed Toggle[cite: 11] */}
        <div style={listStyles.toggleContainer}>
          <button
            style={{ ...listStyles.toggleTab, ...(activeTab === 'ALL' ? listStyles.activeToggleTab : {}) }}
            onClick={() => setActiveTab('ALL')}
          >
            All
          </button>
          <button
            style={{ ...listStyles.toggleTab, ...(activeTab === 'ONGOING' ? listStyles.activeToggleTab : {}) }}
            onClick={() => setActiveTab('ONGOING')}
          >
            Ongoing
            {ongoingCount > 0 && <span style={listStyles.badge}>{ongoingCount}</span>}
          </button>
          <button
            style={{ ...listStyles.toggleTab, ...(activeTab === 'CLOSED' ? listStyles.activeToggleTab : {}) }}
            onClick={() => setActiveTab('CLOSED')}
          >
            Closed ({closedCount})
          </button>
        </div>

        {/* Filter Chips with SVGs[cite: 11] */}
        <div style={listStyles.chipsRow}>
          {filterChips.map((chip) => {
            const Icon = chip.icon;
            const isActive = activeFilter === chip.label;
            return (
              <button
                key={chip.label}
                style={{
                  ...listStyles.filterChip,
                  ...(isActive ? listStyles.activeFilterChip : {}),
                }}
                onClick={() => setActiveFilter(chip.label)}
              >
                {Icon && <img src={Icon} alt="" style={{ width: 13, height: 13 }} />}
                <span>{chip.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Orders List / States[cite: 11] */}
      {isOrdersLoading ? (
        <div style={listStyles.stateContainer}>Loading your orders...</div>
      ) : displayedOrders.length === 0 ? (
        <div style={listStyles.emptyBox}>
          <img src={Package} alt="empty" style={{ width: 80, height: 80, opacity: 0.25, marginBottom: 12 }} />
          <span style={{ fontSize: 15, fontWeight: '600', color: '#2B2A29' }}>No orders found</span>
          <span style={{ fontSize: 13, color: '#707B8C', marginTop: 4 }}>
            {searchQuery ? 'No results matched your search.' : 'You have no orders in this tab yet.'}
          </span>
        </div>
      ) : (
        <div style={listStyles.ordersList}>
          {displayedOrders.map((order) => {
            const key = order.order_id || order._id || order.salesorder_id;
            return (
              <OrderCard
                key={key}
                order={order}
                onPress={() => navigate(`/consumer/account/orders/${key}`)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

const listStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 16 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  pageTitle: { fontSize: 20, color: '#2B2A29', margin: 0, fontWeight: '700' },
  orderCountText: { fontSize: 13, color: '#707B8C', fontWeight: '600' },
  controls: { display: 'flex', flexDirection: 'column', gap: 12 },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 8,
    padding: '8px 12px',
    gap: 8,
  },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#2B2A29', background: 'transparent' },
  clearBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#707B8C', fontSize: 12 },
  toggleContainer: {
    display: 'flex',
    backgroundColor: '#F5F3EF',
    borderRadius: 24,
    padding: 3,
    border: '1px solid #FFFFFF',
    width: 'fit-content',
    gap: 4,
  },
  toggleTab: {
    border: 'none',
    background: 'none',
    padding: '6px 16px',
    borderRadius: 20,
    fontSize: 13,
    color: '#2E4057',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: '600',
  },
  activeToggleTab: { backgroundColor: '#FFFFFF', color: '#2B2A29', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  badge: { backgroundColor: '#E9001E', color: '#FFFFFF', fontSize: 10, borderRadius: 10, padding: '2px 6px', fontWeight: '700' },
  chipsRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: '0.5px solid #CBD5E1',
    borderRadius: 8,
    padding: '6px 14px',
    backgroundColor: '#FFFFFF',
    fontSize: 13,
    color: '#2E4057',
    cursor: 'pointer',
    fontWeight: '500',
  },
  activeFilterChip: { backgroundColor: '#2B2A29', color: '#F5F3EF', borderColor: '#2B2A29' },
  ordersList: { display: 'flex', flexDirection: 'column', gap: 12 },
  stateContainer: { padding: 40, textAlign: 'center', color: '#707B8C', fontSize: 14 },
  emptyBox: { padding: 48, display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #E2E8F0' },
};

const detailsStyles = {
  wrapper: { display: 'flex', flexDirection: 'column', gap: 16 },
  headerRow: { display: 'flex', alignItems: 'center', gap: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, border: '1px solid #CBD5E1', background: '#FFFFFF', cursor: 'pointer', fontSize: 16, fontWeight: '700' },
  orderNumberTitle: { fontSize: 18, color: '#2B2A29', margin: 0, fontWeight: '700' },
  orderDateSubtitle: { fontSize: 12, color: '#707B8C' },
  addressCard: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', overflow: 'hidden' },
  addressCardHeader: { backgroundColor: '#F8F6F2', padding: '10px 16px', borderBottom: '1px solid #CBD5E1' },
  addressCardHeaderTitle: { fontSize: 13, fontWeight: '700', color: '#2B2A29' },
  addressCardContent: { padding: 14 },
  addressText: { margin: '0 0 4px 0', fontSize: 13, color: '#2B2A29' },
  addressPhone: { fontSize: 12, color: '#707B8C' },
  deliveryGroupCard: { display: 'flex', flexDirection: 'column', gap: 12 },
  otpCard: { backgroundColor: '#FFFDF5', border: '1px solid #FDE68A', borderRadius: 12, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  otpHeaderRow: { display: 'flex', alignItems: 'center', gap: 10, flex: 1 },
  otpIconBox: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#FEF3C7', display: 'flex', justifyContent: 'center', alignItems: 'center' },
  otpTitle: { fontSize: 12, color: '#92400E', lineHeight: '16px' },
  otpValueBox: { textAlign: 'right', marginLeft: 16 },
  otpValueLabel: { fontSize: 10, color: '#707B8C', display: 'block' },
  otpValue: { fontSize: 18, fontWeight: '700', color: '#1E8449' },
  trackingCard: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', padding: 16 },
  trackingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  trackingTitle: { fontSize: 14, fontWeight: '700', color: '#2B2A29' },
  etaRow: { display: 'flex', alignItems: 'center', gap: 6 },
  etaText: { fontSize: 13, fontWeight: '600', color: '#1E8449' },
  stepsContainer: { marginTop: 16, display: 'flex', flexDirection: 'column' },
  stepRow: { display: 'flex', gap: 12, minHeight: 48 },
  stepIconCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24 },
  stepIconCircle: { width: 22, height: 22, borderRadius: 11, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1 },
  dotsCol: { display: 'flex', flexDirection: 'column', gap: 3, margin: '3px 0' },
  dot: { width: 3, height: 3, borderRadius: 1.5 },
  stepTextCol: { flex: 1, paddingBottom: 14 },
  stepTitle: { fontSize: 13, fontWeight: '600', display: 'block' },
  stepDesc: { fontSize: 11, color: '#707B8C' },
  itemsBox: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', padding: 16 },
  itemsCountLabel: { fontSize: 13, color: '#2B2A29', fontWeight: '600', display: 'block', marginBottom: 12 },
  itemLine: { display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #F1F5F9' },
  itemThumbBox: { width: 44, height: 44, borderRadius: 6, backgroundColor: '#F8F6F2', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' },
  itemThumbImg: { width: '100%', height: '100%', objectFit: 'contain' },
  itemThumbFallback: {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#FFFFFF',
},
  itemMetaCol: { flex: 1 },
  itemTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  itemTitleText: { fontSize: 13, fontWeight: '600', color: '#2B2A29' },
  itemPriceText: { fontSize: 13, fontWeight: '700', color: '#2B2A29' },
  itemQtyText: { fontSize: 11, color: '#707B8C' },
  summaryCard: { backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #CBD5E1', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 },
  summaryTitle: { fontSize: 14, fontWeight: '700', color: '#2B2A29', margin: '0 0 4px 0' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#2B2A29' },
  receiptDivider: { height: 1, backgroundColor: '#CBD5E1', margin: '6px 0' },
  invoiceButton: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #CBD5E1',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  helpCard: { backgroundColor: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 12, padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  helpText: { fontSize: 13, fontWeight: '600', color: '#2B2A29' },
  helpButtonGroup: { display: 'flex', gap: 8 },
  helpBtn: { display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #CBD5E1', borderRadius: 8, backgroundColor: '#FFFFFF', padding: '6px 14px', fontSize: 12, fontWeight: '600', cursor: 'pointer' },
  loadingContainer: { padding: 48, textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: 12, border: '1px solid #E2E8F0' },
};